import React, {useState, useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  TextField,
  Grid,
  Box,
  CircularProgress,
  Tooltip
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentIcon from '@material-ui/icons/Assignment';
import moment from "moment";
import "moment/locale/es";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const AppointmentActionsModal = ({
  open,
  appointment,
  onClose,
  onCancelAppointment,
  onReprogram,
  onCompleteAppointment,
  mode = 'edit',
  disableReprogram = false,
}) => {
  const isViewMode = mode === 'view';
  const { user: loggedInUser } = useContext(AuthContext); 
  const [notes, setNotes] = useState("");
  const [presionArterial, setPresionArterial] = useState("");
  const [frecuenciaCardiaca, setFrecuenciaCardiaca] = useState("");
  const [temperatura, setTemperatura] = useState("");
  const [saturacionOxigeno, setSaturacionOxigeno] = useState("");
  const [peso, setPeso] = useState("");
  const [talla, setTalla] = useState("");
  const [alergias, setAlergias] = useState("");
  const [motivoConsulta, setMotivoConsulta] = useState("");
  const [edad, setEdad] = useState("");
  const [frecuenciaRespiratoria, setFrecuenciaRespiratoria] = useState("");
  const [glucemiaCapilar, setGlucemiaCapilar] = useState("");
  const [patientName, setPatientName] = useState("");

  const [saving, setSaving] = useState(false);
  const [favoriteTemplateId, setFavoriteTemplateId] = useState(null);
  const [loadingFavorite, setLoadingFavorite] = useState(false);

  useEffect(() => {
    if (appointment) {
      setNotes(appointment.notes || "");
      setPresionArterial(appointment.presionArterial || "");
      setFrecuenciaCardiaca(appointment.frecuenciaCardiaca || "");
      setTemperatura(appointment.temperatura || "");
      setSaturacionOxigeno(appointment.saturacionOxigeno || "");
      setPeso(appointment.peso || "");
      setTalla(appointment.talla || "");
      setAlergias(appointment.alergias || "");
      setMotivoConsulta(appointment.motivoConsulta || "");
      setEdad(appointment.edad || "");
      setFrecuenciaRespiratoria(appointment.frecuenciaRespiratoria || "");
      setGlucemiaCapilar(appointment.glucemiaCapilar || "");
      setPatientName(appointment.patientName || "");
      
      setLoadingFavorite(true);
      api.get("/favorite-template")
        .then(({ data }) => {
          if (data && data.templateId) {
            setFavoriteTemplateId(data.templateId);
          }
        })
        .catch(err => {
          console.error("No se pudo cargar la plantilla favorita", err);
        })
        .finally(() => {
          setLoadingFavorite(false);
        });
    } else {
      setFavoriteTemplateId(null);
    }
  }, [appointment]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedData = {
        notes,
        presionArterial,
        frecuenciaCardiaca: frecuenciaCardiaca ? Number(frecuenciaCardiaca) : null,
        temperatura: temperatura ? Number(temperatura) : null,
        saturacionOxigeno: saturacionOxigeno ? Number(saturacionOxigeno) : null,
        peso: peso ? Number(peso) : null,
        talla: talla ? Number(talla) : null,
        alergias,
        motivoConsulta,
        edad: edad ? Number(edad) : null,
        frecuenciaRespiratoria: frecuenciaRespiratoria ? Number(frecuenciaRespiratoria) : null,
        glucemiaCapilar: glucemiaCapilar ? Number(glucemiaCapilar) : null,
        patientName: patientName.trim() || null,
      };
      await api.patch(`/appointments/${appointment.id}`, updatedData);
      toast.success("Datos de la cita actualizados correctamente.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (saving) return;
    setSaving(true);
    try {
        await onCompleteAppointment(appointment.id);
    } catch (error) {
        console.error(error);
    } finally {
        setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (saving) return;
    setSaving(true);
    try {
        await onCancelAppointment(appointment.id);
    } catch (error) {
        console.error(error);
    } finally {
        setSaving(false);
    }
  };

  const handleReprogram = async () => {
    if (saving) return;
    onReprogram(appointment);
  };

  if (!appointment) return null;
  
  const displayName = appointment.patientName || appointment.contact?.name;
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Detalles de la Cita
        <IconButton
          aria-label="close"
          onClick={() => !saving && onClose()}
          disabled={saving}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="h6">Cita con {displayName}</Typography>
          {appointment.patientName && (
            <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
              Agendado por: {appointment.contact?.name}
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {moment(appointment.date, "YYYY-MM-DD").locale("es").format("dddd, D [de] MMMM [de] YYYY")}
            {" | "}
            {moment(appointment.startTime, "HH:mm:ss").format("HH:mm")} - {moment(appointment.endTime, "HH:mm:ss").format("HH:mm")}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField 
              label="Nombre del Paciente (Opcional)" 
              fullWidth 
              value={patientName} 
              onChange={(e) => setPatientName(e.target.value)} 
              variant="outlined" 
              margin="dense"
              helperText="Dejar vacío si la cita es para el contacto principal."
              disabled={isViewMode || saving}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Motivo de Consulta" fullWidth value={motivoConsulta} onChange={(e) => setMotivoConsulta(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Alergias Conocidas" fullWidth multiline rows={2} value={alergias} onChange={(e) => setAlergias(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Presión Arterial" fullWidth value={presionArterial} onChange={(e) => setPresionArterial(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Frec. Cardíaca" type="number" fullWidth value={frecuenciaCardiaca} onChange={(e) => setFrecuenciaCardiaca(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Frec. Respiratoria" type="number" fullWidth value={frecuenciaRespiratoria} onChange={(e) => setFrecuenciaRespiratoria(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Glucemia Capilar" type="number" fullWidth value={glucemiaCapilar} onChange={(e) => setGlucemiaCapilar(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Temperatura (°C)" type="number" fullWidth value={temperatura} onChange={(e) => setTemperatura(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Sat. Oxígeno (%)" type="number" fullWidth value={saturacionOxigeno} onChange={(e) => setSaturacionOxigeno(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Peso (kg)" type="number" fullWidth value={peso} onChange={(e) => setPeso(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Talla (cm)" type="number" fullWidth value={talla} onChange={(e) => setTalla(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Edad" type="number" fullWidth value={edad} onChange={(e) => setEdad(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notas Adicionales" fullWidth multiline rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} variant="outlined" margin="dense" disabled={isViewMode || saving} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {(loggedInUser.isDoctor) && (
          <Button
            component={RouterLink}
            to={
              favoriteTemplateId
                ? {
                    pathname: "/Prescriptions/Editor/" + appointment.contactId + "/" + favoriteTemplateId,
                    search: "?appointmentId=" + appointment.id,
                    state: { patientName: appointment.patientName }
                  }
                : "/Prescriptions?contactId=" + appointment.contactId + (appointment.patientName ? "&patientName=" + encodeURIComponent(appointment.patientName) : "")
            }
            target="_blank"
            color="primary"
            variant="contained"
            startIcon={<AssignmentIcon />}
            disabled={loadingFavorite || !appointment || saving}
          >
            {loadingFavorite ? <CircularProgress size={20} /> : "Crear Receta"}
          </Button>
        )}
        
        {!isViewMode && (
          <>
            <Button onClick={handleCancel} color="secondary" disabled={saving}>
              Cancelar Cita
            </Button>
            
            {disableReprogram ? (
               <Tooltip title="La reprogramación solo está disponible desde el Calendario">
                  <span>
                    <Button color="primary" disabled>
                      Reprogramar
                    </Button>
                  </span>
               </Tooltip>
            ) : (
               <Button onClick={handleReprogram} color="primary" disabled={saving}>
                 Reprogramar
               </Button>
            )}
          </>
        )}
        
        <Box flexGrow={1} />
        
        {appointment?.status === 'scheduled' && (
          <Button 
            variant="contained" 
            style={{ backgroundColor: '#4caf50', color: 'white' }}
            onClick={handleComplete}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : "Finalizar Cita"}
          </Button>
        )}
        
        {!isViewMode && (
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} color="inherit" /> : "Guardar Cambios"}
          </Button>
        )}

        {isViewMode && (
          <Button onClick={() => onClose()} color="primary" variant="contained">
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentActionsModal;